/**
 * LLM-based Content Analyzer for Visa Screening
 *
 * Design principles for small browser-based LLMs (1-3B params):
 * 1. VERY STRICT PRE-FILTER - Only analyze genuinely suspicious content
 * 2. SIMPLE BINARY QUESTIONS - Ask YES/NO with clear examples
 * 3. DEFAULT TO NO - Only flag if VERY confident
 * 4. SKIP OBVIOUS FALSE POSITIVES - Job postings, immigration resources, etc.
 */

import type { Client } from '@browser-first-ai/sdk';
import type { Message, Article, Profile, Recommendation, Publication, Share, Comment } from '../types.js';
import type { TwitterData } from '../parsers/twitter/types.js';

export interface ContentChunk {
  id: string;
  type: 'message' | 'article' | 'profile' | 'recommendation' | 'publication' | 'share' | 'comment' | 'tweet' | 'dm' | 'like';
  content: string;
  metadata: {
    date?: Date;
    source?: string;
    context?: string;
    platform?: 'linkedin' | 'twitter';
  };
}

export interface LLMFlag {
  severity: 'red' | 'orange';
  category: string;
  content: string;
  reason: string;
  suggestion: string;
  chunkId: string;
}

export interface LLMAnalysisResult {
  flags: LLMFlag[];
  summary: string;
  chunksAnalyzed: number;
  totalChunks: number;
}

interface ContentCheck {
  id: string;
  severity: 'red' | 'orange';
  category: string;
  question: string;
  suggestion: string;
  appliesTo?: ContentChunk['type'][];
}

const CONTENT_CHECKS: ContentCheck[] = [
  {
    id: 'anti-us',
    severity: 'red',
    category: 'Anti-US Sentiment',
    question: `Does this text express genuine HATRED or HOSTILITY toward the United States, its government, or American people?

I will answer YES only if the text contains statements like:
- "I hate America" or "I hate the US"
- "Death to America" or similar violent language
- "The US is evil" or "Americans are terrible"

I will answer NO for:
- Criticizing US policies (immigration, healthcare, etc.) - criticism is fine
- Complaining about work or bureaucracy - frustration is fine
- Mentioning challenges or difficulties - normal conversation
- Any professional, business, or career content
- Job postings or recruiting messages
- Immigration advice or visa discussions

Text: "${'{CONTENT}'}"

Is this genuine hatred/hostility toward the US? Answer only YES or NO:`,
    suggestion: 'Review this content - it may raise concerns with visa officers.',
  },
  {
    id: 'immigration-intent',
    severity: 'orange',
    category: 'Immigration Intent Statement',
    question: `Does this text contain the AUTHOR explicitly stating THEIR OWN intent to never return to their home country or stay in the US forever?

I will answer YES only if the AUTHOR of the text says:
- "I never want to go back to my country"
- "I'm never leaving the US"
- "I want to settle permanently and never return"
- "I don't plan to ever go back home"

I will answer NO for:
- Job postings that offer visa sponsorship - this is normal recruiting
- Immigration advice or resources (EB1A, USCIS, green card info) - this is helpful information
- Discussing legal immigration paths - this is appropriate
- Mentioning "USA" or locations - this is just geography
- Recruiting messages mentioning relocation - this is job offers
- Media mentions (Forbes, USA Today, TechCrunch) - these are publication names
- Questions about locations ("in USA?") - this is just asking
- Any message FROM someone else to the author - we only care about what the AUTHOR says
- Career discussions, networking, professional content

Text: "${'{CONTENT}'}"

Does the AUTHOR explicitly state they never want to return home? Answer only YES or NO:`,
    suggestion: 'Be prepared to explain your ties to your home country if asked.',
    appliesTo: ['message', 'article', 'profile', 'share', 'comment'],
  },
  {
    id: 'work-violation',
    severity: 'red',
    category: 'Work Authorization Concern',
    question: `Does this text describe the AUTHOR working ILLEGALLY or without proper authorization?

I will answer YES only if the text says:
- "I worked without a work visa"
- "They paid me cash under the table illegally"
- "I did paid work while on a tourist visa"

I will answer NO for:
- Discussing visa categories (H1B, EB1A, EB2NIW, etc.) - this is immigration knowledge
- Immigration advice from USCIS or lawyers - this is helpful resources
- Job postings offering visa sponsorship - this is legal employment
- Mentioning "visa" in any job or immigration context - this is normal
- Volunteer work or unpaid projects - these don't require work authorization
- Any discussion of LEGAL immigration or work authorization

Text: "${'{CONTENT}'}"

Does this describe illegal/unauthorized work? Answer only YES or NO:`,
    suggestion: 'This may raise concerns. Consider consulting an immigration attorney.',
  },
];

/**
 * Create content chunks from LinkedIn data
 */
export function chunkContent(
  messages: Message[],
  articles: Article[],
  profile: Profile | null,
  _recommendations: Recommendation[],
  publications: Publication[],
  shares: Share[] = [],
  comments: Comment[] = []
): ContentChunk[] {
  const chunks: ContentChunk[] = [];
  const MAX_CHUNK = 500;

  // Process messages
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg.content?.trim()) continue;

    const content = msg.content.length > MAX_CHUNK
      ? msg.content.substring(0, MAX_CHUNK) + '...'
      : msg.content;

    chunks.push({
      id: `msg-${i}`,
      type: 'message',
      content,
      metadata: {
        date: msg.date || undefined,
        source: msg.from || 'Unknown',
        context: 'LinkedIn Message',
      },
    });
  }

  // Process shares/posts
  for (let i = 0; i < shares.length; i++) {
    const share = shares[i];
    if (!share.shareCommentary?.trim()) continue;

    const content = share.shareCommentary.length > MAX_CHUNK
      ? share.shareCommentary.substring(0, MAX_CHUNK) + '...'
      : share.shareCommentary;

    chunks.push({
      id: `share-${i}`,
      type: 'share',
      content,
      metadata: {
        date: share.date || undefined,
        source: 'LinkedIn Post',
        context: 'LinkedIn Post',
      },
    });
  }

  // Process comments
  for (let i = 0; i < comments.length; i++) {
    const comment = comments[i];
    if (!comment.message?.trim()) continue;

    const content = comment.message.length > MAX_CHUNK
      ? comment.message.substring(0, MAX_CHUNK) + '...'
      : comment.message;

    chunks.push({
      id: `comment-${i}`,
      type: 'comment',
      content,
      metadata: {
        date: comment.date || undefined,
        source: 'LinkedIn Comment',
        context: 'LinkedIn Comment',
      },
    });
  }

  // Process articles
  for (let i = 0; i < articles.length; i++) {
    const article = articles[i];
    const fullContent = `${article.title || ''}\n${article.content || ''}`.trim();
    if (!fullContent) continue;

    const articleChunks = splitIntoChunks(fullContent, MAX_CHUNK);
    articleChunks.forEach((chunk, j) => {
      chunks.push({
        id: `article-${i}-${j}`,
        type: 'article',
        content: chunk,
        metadata: {
          date: article.publishedOn || article.createdOn || undefined,
          source: article.title || 'Article',
          context: 'LinkedIn Article',
        },
      });
    });
  }

  // Process profile
  if (profile?.summary?.trim()) {
    const profileContent = `${profile.headline || ''}\n${profile.summary}`.trim();
    const profileChunks = splitIntoChunks(profileContent, MAX_CHUNK);
    profileChunks.forEach((chunk, i) => {
      chunks.push({
        id: `profile-${i}`,
        type: 'profile',
        content: chunk,
        metadata: { context: 'LinkedIn Profile' },
      });
    });
  }

  // Process publications
  for (let i = 0; i < publications.length; i++) {
    const pub = publications[i];
    const content = `${pub.name}\n${pub.description || ''}`.trim();
    if (!content) continue;

    chunks.push({
      id: `pub-${i}`,
      type: 'publication',
      content: content.length > MAX_CHUNK ? content.substring(0, MAX_CHUNK) + '...' : content,
      metadata: {
        date: pub.publishedOn || undefined,
        source: pub.name,
        context: 'LinkedIn Publication',
      },
    });
  }

  return chunks;
}

function splitIntoChunks(text: string, maxLen: number): string[] {
  if (text.length <= maxLen) return [text];

  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    if (remaining.length <= maxLen) {
      chunks.push(remaining);
      break;
    }

    let splitPoint = remaining.lastIndexOf('. ', maxLen);
    if (splitPoint === -1 || splitPoint < maxLen / 2) {
      splitPoint = remaining.lastIndexOf(' ', maxLen);
    }
    if (splitPoint === -1 || splitPoint < maxLen / 2) {
      splitPoint = maxLen;
    }

    chunks.push(remaining.substring(0, splitPoint + 1).trim());
    remaining = remaining.substring(splitPoint + 1).trim();
  }

  return chunks;
}

/**
 * Create content chunks from Twitter data
 */
export function chunkTwitterContent(data: TwitterData): ContentChunk[] {
  const chunks: ContentChunk[] = [];
  const MAX_CHUNK = 500;

  // Process tweets (most important - public posts)
  for (let i = 0; i < data.tweets.length; i++) {
    const tweet = data.tweets[i];
    if (!tweet.text?.trim()) continue;

    // Skip retweets (not original content)
    if (tweet.isRetweet) continue;

    const content = tweet.text.length > MAX_CHUNK
      ? tweet.text.substring(0, MAX_CHUNK) + '...'
      : tweet.text;

    chunks.push({
      id: `tweet-${i}`,
      type: 'tweet',
      content,
      metadata: {
        date: tweet.createdAt || undefined,
        source: `@${data.account?.username || 'unknown'}`,
        context: 'Twitter Post',
        platform: 'twitter',
      },
    });
  }

  // Process DMs (only messages FROM the user)
  for (let i = 0; i < data.directMessages.length; i++) {
    const dm = data.directMessages[i];
    if (!dm.text?.trim() || !dm.isFromUser) continue;

    const content = dm.text.length > MAX_CHUNK
      ? dm.text.substring(0, MAX_CHUNK) + '...'
      : dm.text;

    chunks.push({
      id: `dm-${i}`,
      type: 'dm',
      content,
      metadata: {
        date: dm.createdAt || undefined,
        source: 'Twitter DM',
        context: 'Twitter Direct Message',
        platform: 'twitter',
      },
    });
  }

  // Process likes (shows endorsement of content)
  for (let i = 0; i < Math.min(data.likes.length, 100); i++) { // Limit to 100 likes
    const like = data.likes[i];
    if (!like.fullText?.trim()) continue;

    const content = like.fullText.length > MAX_CHUNK
      ? like.fullText.substring(0, MAX_CHUNK) + '...'
      : like.fullText;

    chunks.push({
      id: `like-${i}`,
      type: 'like',
      content,
      metadata: {
        source: 'Liked Tweet',
        context: 'Twitter Like',
        platform: 'twitter',
      },
    });
  }

  // Process profile bio
  if (data.profile?.bio?.trim()) {
    chunks.push({
      id: 'twitter-profile',
      type: 'profile',
      content: data.profile.bio,
      metadata: {
        context: 'Twitter Profile Bio',
        platform: 'twitter',
      },
    });
  }

  return chunks;
}

/**
 * STRICT pre-filter - only pass through genuinely suspicious content.
 * Excludes obvious false positives like job postings, immigration resources, etc.
 */
export function preFilterChunks(chunks: ContentChunk[]): ContentChunk[] {
  // Phrases that indicate content we should SKIP (false positives)
  const skipPatterns = [
    // Job postings offering visa support
    /will sponsor|visa sponsor|support visa|visa support|relocation support|relocation assistance/i,
    // Immigration resources and advice
    /uscis\.gov|uscis website|eb1a|eb2niw|eb-1|eb-2|immigration lawyer|immigration attorney/i,
    // Media/publication names
    /usa today|techcrunch|forbes|inc\.com|business insider/i,
    // Recruiting/job messages
    /looking for.*engineer|hiring.*developer|open position|job opportunity|we're hiring/i,
    // Questions about location
    /\?.*usa|\?.*america|in usa\?|in the us\?/i,
  ];

  // Phrases that genuinely warrant analysis
  const analyzePatterns = [
    // Actual concerning phrases
    /hate america|hate the us|hate the usa|death to america/i,
    /never go back|never going back|never return|not going back/i,
    /stay forever|never leave|settle permanent|permanent.*never/i,
    /work without visa|worked illegally|under the table.*pay|paid.*under the table/i,
  ];

  return chunks.filter(chunk => {
    // First check if it matches skip patterns - if so, exclude
    for (const pattern of skipPatterns) {
      if (pattern.test(chunk.content)) {
        return false;
      }
    }

    // Then check if it matches analyze patterns - if so, include
    for (const pattern of analyzePatterns) {
      if (pattern.test(chunk.content)) {
        return true;
      }
    }

    // Default: don't analyze
    return false;
  });
}

/**
 * Run a single yes/no check on content
 */
async function runCheck(
  client: Client,
  chunk: ContentChunk,
  check: ContentCheck
): Promise<{ flagged: boolean; content: string } | null> {
  try {
    const prompt = check.question.replace('{CONTENT}', chunk.content);

    const response = await client.chat({
      model: 'auto',
      messages: [{ role: 'user', content: prompt }],
    });

    const answer = response.content.trim().toUpperCase();

    // STRICT: Only accept explicit YES (not "YES, but..." or "YES however...")
    if (answer === 'YES' || answer === 'YES.') {
      return { flagged: true, content: chunk.content };
    }

    return { flagged: false, content: chunk.content };
  } catch (err) {
    console.warn(`Check "${check.id}" failed:`, err);
    return null;
  }
}

/**
 * Analyze content with LLM
 */
export async function analyzeContentWithLLM(
  client: Client,
  chunks: ContentChunk[],
  onProgress?: (analyzed: number, total: number, currentFlag?: LLMFlag) => void
): Promise<LLMAnalysisResult> {
  const flags: LLMFlag[] = [];
  const prioritized = prioritizeChunks(chunks);

  console.log(`LLM Analysis: ${prioritized.length} chunks after prioritization`);

  let analyzed = 0;
  const totalOps = prioritized.length * CONTENT_CHECKS.length;

  for (const chunk of prioritized) {
    for (const check of CONTENT_CHECKS) {
      if (check.appliesTo && !check.appliesTo.includes(chunk.type)) {
        continue;
      }

      analyzed++;
      onProgress?.(analyzed, totalOps);

      const result = await runCheck(client, chunk, check);

      if (result?.flagged) {
        const flag: LLMFlag = {
          severity: check.severity,
          category: check.category,
          content: result.content,
          reason: `Flagged by ${check.category} check`,
          suggestion: check.suggestion,
          chunkId: chunk.id,
        };
        flags.push(flag);
        onProgress?.(analyzed, totalOps, flag);

        if (check.severity === 'red') break;
      }
    }
  }

  const uniqueFlags = deduplicateFlags(flags);
  const redCount = uniqueFlags.filter(f => f.severity === 'red').length;
  const orangeCount = uniqueFlags.filter(f => f.severity === 'orange').length;

  let summary: string;
  if (redCount === 0 && orangeCount === 0) {
    summary = 'No concerning content found. Your LinkedIn profile appears appropriate for visa interviews.';
  } else if (redCount > 0) {
    summary = `Found ${redCount} critical issue${redCount > 1 ? 's' : ''} and ${orangeCount} item${orangeCount !== 1 ? 's' : ''} to review.`;
  } else {
    summary = `Found ${orangeCount} item${orangeCount !== 1 ? 's' : ''} you may want to review.`;
  }

  return {
    flags: uniqueFlags,
    summary,
    chunksAnalyzed: prioritized.length,
    totalChunks: chunks.length,
  };
}

function prioritizeChunks(chunks: ContentChunk[]): ContentChunk[] {
  // LinkedIn content
  const profile = chunks.filter(c => c.type === 'profile');
  const articles = chunks.filter(c => c.type === 'article');
  const shares = chunks.filter(c => c.type === 'share');
  const comments = chunks.filter(c => c.type === 'comment');
  const publications = chunks.filter(c => c.type === 'publication');
  const messages = chunks.filter(c => c.type === 'message');

  // Twitter content
  const tweets = chunks.filter(c => c.type === 'tweet');
  const dms = chunks.filter(c => c.type === 'dm');
  const likes = chunks.filter(c => c.type === 'like');

  const sortByDate = (a: ContentChunk, b: ContentChunk) =>
    (b.metadata.date?.getTime() || 0) - (a.metadata.date?.getTime() || 0);

  return [
    // Profiles first (both platforms)
    ...profile,
    // LinkedIn public content
    ...articles.slice(0, 10),
    ...shares.sort(sortByDate).slice(0, 50),
    ...comments.sort(sortByDate).slice(0, 30),
    ...publications.slice(0, 10),
    // Twitter public content
    ...tweets.sort(sortByDate).slice(0, 100),
    ...likes.slice(0, 50), // Likes show endorsement
    // Private messages (both platforms)
    ...messages.sort(sortByDate).slice(0, 100),
    ...dms.sort(sortByDate).slice(0, 50),
  ];
}

function deduplicateFlags(flags: LLMFlag[]): LLMFlag[] {
  const byChunk = new Map<string, LLMFlag>();
  for (const flag of flags) {
    const existing = byChunk.get(flag.chunkId);
    if (!existing || (flag.severity === 'red' && existing.severity !== 'red')) {
      byChunk.set(flag.chunkId, flag);
    }
  }
  return Array.from(byChunk.values());
}
